import { Modal, Select, Radio, Button, List, Tag, Space, Typography, App, Spin, Empty } from "antd";
import { Trash2, Mail, MailCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetAssignableAssessors,
  useGetAssignments,
  useAssignAssessors,
  useUnassignAssessor,
} from "@hooks";
import { PendingCorrection } from "../../types/grading";

interface Props {
  open: boolean;
  onClose: () => void;
  correction: PendingCorrection | null;
}

type Scope = "term" | "instance";

/**
 * Modal for system administrators to assign assessors to grade an exam.
 * Supports both term-level (whole exam term) and instance-level (single candidate) assignment.
 */
export const AssignAssessorsModal = ({ open, onClose, correction }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [scope, setScope] = useState<Scope>("term");
  const [selectedAssessorIds, setSelectedAssessorIds] = useState<string[]>([]);

  const { data: assessors, isLoading: assessorsLoading } = useGetAssignableAssessors({
    enabled: open,
  });
  const { data: assignments, isLoading: assignmentsLoading } = useGetAssignments(
    {
      examTermId: correction?.examTermId,
      examInstanceId: correction?.examInstanceId,
    },
    { enabled: open }
  );
  const assign = useAssignAssessors();
  const unassign = useUnassignAssessor();

  useEffect(() => {
    if (open) {
      setScope("term");
      setSelectedAssessorIds([]);
    }
  }, [open, correction?.examInstanceId]);

  const handleAssign = () => {
    if (!correction || selectedAssessorIds.length === 0) {
      return;
    }

    assign.mutate(
      {
        assessorIds: selectedAssessorIds,
        examTermId: scope === "term" ? correction.examTermId : null,
        examInstanceId: scope === "instance" ? correction.examInstanceId : null,
      },
      {
        onSuccess: (result) => {
          message.success(result.message || t("grading.assign.success"));
          setSelectedAssessorIds([]);
        },
        onError: () => message.error(t("grading.assign.error")),
      }
    );
  };

  const handleUnassign = (assignmentId: string) => {
    unassign.mutate(assignmentId, {
      onSuccess: () => message.success(t("grading.assign.removed")),
      onError: () => message.error(t("grading.assign.error")),
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("grading.assign.title")}
      okText={t("grading.assign.assignButton")}
      cancelText={t("close")}
      onOk={handleAssign}
      okButtonProps={{ disabled: selectedAssessorIds.length === 0, loading: assign.isPending }}
      width={620}
    >
      {correction && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            {correction.examTitle} — {correction.participantName}
          </Typography.Text>

          <div>
            <Typography.Text strong>{t("grading.assign.scope")}</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
                <Radio.Button value="term">{t("grading.assign.scopeTerm")}</Radio.Button>
                <Radio.Button value="instance">{t("grading.assign.scopeInstance")}</Radio.Button>
              </Radio.Group>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {scope === "term"
                ? t("grading.assign.scopeTermHint")
                : t("grading.assign.scopeInstanceHint")}
            </Typography.Text>
          </div>

          <div>
            <Typography.Text strong>{t("grading.assign.selectAssessors")}</Typography.Text>
            <Select
              mode="multiple"
              style={{ width: "100%", marginTop: 4 }}
              placeholder={t("grading.assign.selectPlaceholder")}
              loading={assessorsLoading}
              value={selectedAssessorIds}
              onChange={setSelectedAssessorIds}
              optionFilterProp="label"
              options={(assessors ?? []).map((a) => ({
                label: `${a.assessorName}${a.assessorEmail ? ` (${a.assessorEmail})` : ""}`,
                value: a.assessorId,
              }))}
            />
          </div>

          <div>
            <Typography.Text strong>{t("grading.assign.currentAssignments")}</Typography.Text>
            <Spin spinning={assignmentsLoading}>
              {assignments && assignments.length > 0 ? (
                <List
                  size="small"
                  dataSource={assignments}
                  renderItem={(a) => (
                    <List.Item
                      actions={[
                        <Button
                          key="remove"
                          type="text"
                          danger
                          size="small"
                          icon={<Trash2 size={16} />}
                          loading={unassign.isPending}
                          onClick={() => handleUnassign(a.id)}
                        />,
                      ]}
                    >
                      <Space>
                        <span>{a.assessorName}</span>
                        <Tag color={a.level === "Term" ? "gold" : "blue"}>
                          {a.level === "Term"
                            ? t("grading.assign.scopeTerm")
                            : t("grading.assign.scopeInstance")}
                        </Tag>
                        {a.notificationSent ? (
                          <MailCheck size={14} color="#52c41a" />
                        ) : (
                          <Mail size={14} color="#bfbfbf" />
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("grading.assign.noAssignments")}
                />
              )}
            </Spin>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default AssignAssessorsModal;
